import { useRef, useState, useCallback, useEffect } from 'react';

interface UseCollapsibleCopyOptions {
  collapsedLines?: number;
  isListContent?: boolean;
  initialShouldTruncate?: boolean;
}

interface UseCollapsibleCopyReturn {
  copyRef: (node: HTMLDivElement | null) => void;
  isExpanded: boolean;
  shouldTruncate: boolean;
  toggleExpanded: () => void;
}

function countListItems(element: HTMLDivElement): number {
  const lists = element.querySelectorAll(':scope > ul, :scope > ol');
  if (lists.length === 0) return 0;

  return Array.from(lists).reduce((count, list) => count + list.querySelectorAll(':scope > li').length, 0);
}

function countParagraphLines(element: HTMLDivElement): number {
  const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
  if (Number.isNaN(lineHeight) || lineHeight <= 0) return 0;
  return Math.round(element.scrollHeight / lineHeight);
}

function applyListItemVisibility(element: HTMLDivElement, maxItems: number, collapsed: boolean) {
  const lists = element.querySelectorAll('ul, ol');
  if (lists.length === 0) return;

  lists.forEach(list => {
    const listItems = list.querySelectorAll(':scope > li');
    listItems.forEach((li, i) => {
      (li as HTMLElement).style.display = collapsed && i >= maxItems ? 'none' : '';
    });
  });
}

export function useCollapsibleCopy({
  collapsedLines,
  isListContent = false,
  initialShouldTruncate,
}: UseCollapsibleCopyOptions): UseCollapsibleCopyReturn {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(initialShouldTruncate ?? Boolean(collapsedLines));

  const measure = useCallback(() => {
    if (!elementRef.current) {
      setShouldTruncate(false);
      return;
    }

    if (!collapsedLines) {
      setShouldTruncate(false);
      if (isListContent) {
        applyListItemVisibility(elementRef.current, 0, false);
      }
      return;
    }

    // Lists collapse by item count; paragraphs collapse by rendered line count.
    const totalItems = isListContent ? countListItems(elementRef.current) : countParagraphLines(elementRef.current);

    const needsTruncation = totalItems > collapsedLines;
    setShouldTruncate(needsTruncation);

    if (isListContent) {
      applyListItemVisibility(elementRef.current, collapsedLines, needsTruncation && !isExpanded);
    }
  }, [collapsedLines, isListContent, isExpanded]);

  const copyRef = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      measure();
    },
    [measure],
  );

  useEffect(() => {
    if (!elementRef.current) return;

    if (!collapsedLines) {
      if (isListContent) {
        applyListItemVisibility(elementRef.current, 0, false);
      }
      return;
    }

    if (isListContent) {
      applyListItemVisibility(elementRef.current, collapsedLines, shouldTruncate && !isExpanded);
    }
  }, [isExpanded, shouldTruncate, isListContent, collapsedLines]);

  useEffect(() => {
    if (!collapsedLines) return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, collapsedLines]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return {
    copyRef,
    isExpanded,
    shouldTruncate,
    toggleExpanded,
  };
}
